import {generateUUID} from '@assets/helpers/genarateUUID';
import {isEmpty} from '@avada/utils';
import {getCookie, setCookie} from '@scripttag/helpers/cookieHelpers';
import {
  getSessionStorageData,
  getStorageData,
  removeSessionStorageData,
  removeStorageData,
  setSessionStorageData,
  setStorageData
} from '@scripttag/helpers/storageHelpers';

const CHAT_SESSION_KEY = 'avada-chatty/session';
const CHAT_CUSTOMER_KEY = 'avada-chatty/customer';
export const CHAT_SESSION_LIFETIME = 1000 * 60 * 60 * 24 * 7; // 1 week
const DEFAULT_EXPIRATION = 1000 * 24 * 60 * 60 * 30; // 30 days
export const WIDTH_COL_SESSION_KEY = 'avada-chatty/inbox-width';
export const WIDGET_PAGE_SETTINGS = 'avada-chatty/widget-page-setting';
export const EXPAND_STATUS_SESSION_KEY = 'avada-chatty/expand-status';
export const CHAT_ATTRACT = 'avada-chatty/attract-chat';
export const FIRST_OPEN_WIDGET = 'avada-chatty/first-open-widget';
export const AI_GUIDE_WIDGET = 'avada-chatty/ai-guide-widget';

/**
 * Find session ID from local storage
 *
 * @returns {string|null}
 */
export function getChatSessionId(customerId = window.AVADA_APP_FAQS_CUSTOMER?.id) {
  return customerId || getSessionData(CHAT_SESSION_KEY);
}

/**
 * Clear/remove the chat session ID from storage
 * @returns {void}
 */
export function removeChatSessionId() {
  setSessionData(CHAT_SESSION_KEY, null);
}

/**
 * Create chat instance, run when visitor open chat page
 *
 * @returns {string}
 */
export function initChatSessionId(customerId, isReset = false) {
  const id = getChatSessionId(customerId);

  if (id && !isReset) return id;
  if (isReset) removeChatSessionId();

  const newId = generateUUID();
  setSessionData(CHAT_SESSION_KEY, newId);
  return newId;
}

export function getSessionData(key, blank = null) {
  return getCookie(key) || blank;
}

function setSessionData(key, value) {
  setCookie(key, value, DEFAULT_EXPIRATION);
}

export function resetCookieExpiration(key = CHAT_SESSION_KEY, newExpiration = DEFAULT_EXPIRATION) {
  const value = getCookie(key);
  if (value) setCookie(key, value, newExpiration);
}

/**
 * @returns {string}
 */
export function getCustomerDraft(sessionId) {
  const {id, draft = ''} = getStorageData(CHAT_CUSTOMER_KEY);
  if (id === sessionId) return draft;

  setStorageData(CHAT_CUSTOMER_KEY, {id: sessionId});
  return '';
}

/**
 * @param {string} draft
 */
export function setCustomerDraft(draft) {
  setStorageData(CHAT_CUSTOMER_KEY, {...getStorageData(CHAT_CUSTOMER_KEY), draft});
}

/**
 * @param key
 * @returns {*}
 */
export function getStorageWithExpiration(key) {
  if (getStorageData(key + '/expiredAt', 0) >= Date.now()) {
    return getStorageData(key);
  }

  removeStorageData(key);
  removeStorageData(key + '/expiredAt');
  return null;
}

/**
 * @param key
 * @param value
 * @param expires
 */
export function setStorageWithExpiration(key, value, expires = DEFAULT_EXPIRATION) {
  setStorageData(key, value);
  setStorageData(key + '/expiredAt', Date.now() + expires);
}

/**
 * @param key
 * @returns {*}
 */
export function getSessionWithExpiration(key) {
  if (getSessionStorageData(key + '/expiredAt', 0) >= Date.now()) {
    return getSessionStorageData(key);
  }

  removeSessionStorageData(key);
  removeSessionStorageData(key + '/expiredAt');
  return null;
}

/**
 * @param key
 * @param value
 * @param expires
 */
export function setSessionWithExpiration(key, value, expires = DEFAULT_EXPIRATION) {
  setSessionStorageData(key, value);
  setSessionStorageData(key + '/expiredAt', Date.now() + expires);
}

/**
 * @returns {{}}
 */
export const getWidgetSettingsStorage = () => {
  if (window.innerWidth < 768 && !window.location.hash) return {}; // Skip auto open chatbox on mobile https://avadaio.slack.com/archives/C07AZNGEVTM/p1740729878831639
  const data = getStorageWithExpiration(WIDGET_PAGE_SETTINGS);
  return isEmpty(data) ? {} : JSON.parse(data);
};

export const setWidgetSettingsStorage = toUpdate => {
  setStorageWithExpiration(
    WIDGET_PAGE_SETTINGS,
    JSON.stringify({...getWidgetSettingsStorage(), ...toUpdate})
  );
};

export const getChatAttract = () => getStorageData(CHAT_ATTRACT, false);
export const setChatAttract = () => setStorageData(CHAT_ATTRACT, true);

export const getFirstOpenWidget = () => getSessionStorageData(FIRST_OPEN_WIDGET, false);
export const setFirstOpenWidget = () => setSessionStorageData(FIRST_OPEN_WIDGET, true);

export const getCloseAiGuideWidget = () => getSessionStorageData(AI_GUIDE_WIDGET, false);
export const setCloseAiGuideWidget = () => setSessionStorageData(AI_GUIDE_WIDGET, true);
